import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Star, Upload, Flag, Mail, Send, Lock, BadgeCheck, Gamepad2, Package, Landmark, Users as UsersIcon, Clock } from "lucide-react";
import PortalNav from "@/components/PortalNav";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Community Profile v2 — the cross-server citizen identity. Own view: avatar/banner
   uploads (AI-scanned before they go live), bio/flair/accent, per-section privacy
   toggles. Public view: Citizen Level, verified studio badge, staff roles, owned
   servers, workshop mods, public Steam stats + profile link, report/message/invite. */

const PRIVACY_LABELS = [
  ["show_citizens", "Linked citizens", "Which servers your characters live on"],
  ["show_steam", "Steam stats", "Public Steam persona, playtime and profile link"],
  ["show_activity", "Activity detail", "Post counts, sales and reviews in your level breakdown"],
  ["show_worth", "Net worth", "Combined bank across servers"],
];

export default function CommunityProfilePage() {
  const { userId } = useParams();
  const mine = !userId;
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [report, setReport] = useState("");
  const [myServers, setMyServers] = useState([]);
  const [inviteServer, setInviteServer] = useState("");
  const avatarRef = useRef(null);
  const bannerRef = useRef(null);

  const load = useCallback(() => {
    api.get(mine ? "/nc/community/profile/me" : `/nc/community/profile/${userId}`)
      .then((r) => { setProfile(r.data.profile); setErr(""); })
      .catch((e) => { setErr(e.response?.status === 401 ? "Sign in to see your citizen profile." : "No such citizen on the hub."); });
  }, [mine, userId]);
  useEffect(load, [load]);
  useEffect(() => {
    if (!mine) api.get("/nc/servers/mine").then((r) => setMyServers((r.data.servers || []).filter((s) => s.owner))).catch(() => {});
  }, [mine]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/nc/community/profile", { bio: profile.bio, flair: profile.flair, accent: profile.accent, privacy: profile.privacy });
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/nc/community/profile/image?kind=${kind}`, fd);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload rejected"); }
    finally { setBusy(false); }
  };

  const sendMessage = async () => {
    if (!msg.trim()) return;
    try { await api.post("/nc/community/message", { to_user_id: userId, body: msg.trim() }); toast.success("Sent — lands in their main-menu inbox"); setMsg(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send"); }
  };
  const sendInvite = async () => {
    if (!inviteServer) return;
    try { await api.post("/nc/community/invite", { to_user_id: userId, server_id: inviteServer }); toast.success("Invite sent to their main-menu inbox"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't invite"); }
  };
  const sendReport = async () => {
    if (report.trim().length < 3) return;
    try { await api.post("/nc/community/report", { target_user_id: userId, reason: report.trim() }); toast.success("Report received — studio staff review every one"); setReport(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't report"); }
  };

  if (err) return <div className="min-h-screen bg-[#050505] text-zinc-100"><PortalNav title="Citizen Profile" accent="text-nc-blue" /><p className="font-mono text-xs text-zinc-500 mt-16 text-center">{err}</p></div>;
  if (!profile) return <div className="min-h-screen bg-[#050505]" />;
  const acc = profile.accent || "#2E6BFF";
  const lvl = profile.level || { level: 0, xp: 0, next_at: 100, progress: 0 };
  const b = lvl.breakdown || {};

  return (
    <div data-testid="community-profile" className="min-h-screen bg-[#050505] text-zinc-100">
      <PortalNav title="Citizen Profile" accent="text-nc-blue" />

      {/* banner */}
      <div className="relative h-44 sm:h-56 border-b border-white/10 overflow-hidden" style={{ background: `linear-gradient(120deg, ${acc}33, #050505 70%)` }}>
        {profile.banner && <img src={profile.banner} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />}
        {mine && <button data-testid="upload-banner" onClick={() => bannerRef.current?.click()} className="absolute bottom-3 right-3 font-mono text-[9px] uppercase tracking-[0.2em] border border-white/20 bg-black/60 px-3 py-2 hover:border-white/50 transition-colors flex items-center gap-1.5"><Upload className="w-3 h-3" /> Banner</button>}
        <input ref={bannerRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload("banner", e.target.files?.[0])} />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-12 relative z-10 flex flex-col gap-5 pb-16">
        {/* identity card */}
        <div className="flex items-end gap-4 flex-wrap">
          <div className="relative w-24 h-24 rounded-full border-4 border-[#050505] bg-[#101418] flex items-center justify-center overflow-hidden shrink-0" style={{ boxShadow: `0 0 30px ${acc}44` }}>
            {profile.avatar ? <img src={profile.avatar} alt="" className="w-full h-full object-cover" /> : <span className="font-bold text-3xl text-zinc-600">{(profile.name || "?")[0]}</span>}
            {mine && <button data-testid="upload-avatar" onClick={() => avatarRef.current?.click()} className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center"><Upload className="w-5 h-5 text-white" /></button>}
            <input ref={avatarRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload("avatar", e.target.files?.[0])} />
          </div>
          <div className="flex-1 min-w-56">
            <p className="font-sans font-bold text-2xl text-zinc-50 flex items-center gap-2 flex-wrap">
              {profile.name}
              {profile.verified_studio && <span data-testid="verified-badge" className="inline-flex items-center gap-1 px-2 py-0.5 border border-sky-400/60 text-sky-400 font-mono text-[8px] uppercase tracking-[0.15em]"><BadgeCheck className="w-3 h-3" /> SpecPixel Staff</span>}
            </p>
            <p className="font-mono text-[10px] text-zinc-400 mt-0.5">{profile.flair || "Citizen of the network"}</p>
          </div>
          {/* citizen level */}
          <div data-testid="level-card" className="border px-5 py-3 bg-black/50" style={{ borderColor: `${acc}66` }}>
            <p className="font-mono text-[8px] uppercase tracking-[0.25em]" style={{ color: acc }}>Citizen Level</p>
            <p className="font-bold text-3xl text-zinc-50">{lvl.level}</p>
            <div className="w-36 h-1 bg-white/10 mt-1.5"><div className="h-full transition-all" style={{ width: `${(lvl.progress || 0) * 100}%`, backgroundColor: acc }} /></div>
            <p className="font-mono text-[8px] text-zinc-500 mt-1">{lvl.xp} / {lvl.next_at} XP</p>
          </div>
          {/* detective fame */}
          {profile.detective && (
            <div data-testid="detective-card" className="border border-amber-500/40 bg-amber-500/[0.06] px-5 py-3">
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-amber-400">Hall of Detectives</p>
              <p className="font-bold text-3xl text-zinc-50">#{profile.detective.rank}</p>
              <p className="font-mono text-[8px] text-zinc-400 mt-1">{profile.detective.cases} {profile.detective.cases === 1 ? "case" : "cases"} closed · ${profile.detective.bounties?.toLocaleString()} earned</p>
            </div>
          )}
        </div>

        {/* stat grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            [Landmark, "Servers owned", profile.owned_servers?.length ?? 0],
            [ShieldCheck, "Staff roles", profile.staff?.length ?? 0],
            [Package, "Mods live", profile.mods?.length ?? 0],
            [Star, "Reviews received", b.reviews ?? 0],
            [UsersIcon, "Cities joined", b.servers ?? 0],
            [Gamepad2, "Posts on the wire", b.posts ?? 0],
            [Clock, "Steam playtime", profile.steam?.hours != null ? `${profile.steam.hours}h · ${profile.steam.games} games` : profile.steam ? "private" : "unlinked"],
            [BadgeCheck, "Dealer sales", b.sales ?? 0],
          ].map(([Icon, k, v], i) => (
            <div key={k} data-testid={`stat-${i}`} className="border border-white/10 bg-black/40 p-3.5">
              <p className="font-bold text-lg text-zinc-50 flex items-center gap-2 truncate"><Icon className="w-3.5 h-3.5 shrink-0" style={{ color: acc }} />{v}</p>
              <p className="font-mono text-[8px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">{k}</p>
            </div>
          ))}
        </div>

        {/* steam link */}
        {profile.steam?.url && (
          <a data-testid="steam-link" href={profile.steam.url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 border border-[#66c0f4]/30 bg-[#66c0f4]/5 px-4 py-3 hover:bg-[#66c0f4]/10 transition-colors w-fit">
            <img src={profile.steam.avatar} alt="" className="w-8 h-8 rounded" />
            <span className="font-mono text-[10px] text-[#9dcced]">{profile.steam.persona} on Steam — view profile ↗</span>
          </a>
        )}

        {/* staff + owned servers */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="border border-white/10 bg-black/40 p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Staff of</p>
            {(profile.staff || []).map((s, i) => <p key={i} className="font-mono text-[10px] text-zinc-300 py-1 border-b border-white/5">{s.server} <span className="text-zinc-500">· {s.role}</span></p>)}
            {!profile.staff?.length && <p className="font-mono text-[9px] text-zinc-600">No staff badges</p>}
          </div>
          <div className="border border-white/10 bg-black/40 p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Owns</p>
            {(profile.owned_servers || []).map((s) => <p key={s.id} className="font-mono text-[10px] text-zinc-300 py-1 border-b border-white/5">{s.name}</p>)}
            {!profile.owned_servers?.length && <p className="font-mono text-[9px] text-zinc-600">No servers</p>}
          </div>
        </div>

        {/* citizens across the network */}
        {(profile.citizens || []).length > 0 && (
          <div className="border border-white/10 bg-black/40 p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Citizens across the network</p>
            <div className="flex flex-wrap gap-2">
              {profile.citizens.map((c, i) => <span key={i} className="font-mono text-[9px] border border-white/10 px-2.5 py-1.5 text-zinc-300">{c.name} <span className="text-zinc-600">· {c.server}</span></span>)}
            </div>
          </div>
        )}

        {/* own profile: editor + privacy */}
        {mine && profile.privacy && (
          <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-4" data-testid="profile-editor">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: acc }}>Edit profile</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase block mb-1.5">Flair</span>
                <input data-testid="edit-flair" maxLength={40} value={profile.flair || ""} onChange={(e) => setProfile({ ...profile, flair: e.target.value })} className="w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-white/30" placeholder="Night-shift paramedic · 3 cities" /></div>
              <div><span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase block mb-1.5">Accent</span>
                <input data-testid="edit-accent" maxLength={7} value={acc} onChange={(e) => setProfile({ ...profile, accent: e.target.value })} className="w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs focus:outline-none" /></div>
              <div className="sm:col-span-2"><span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase block mb-1.5">Bio</span>
                <textarea data-testid="edit-bio" rows={3} maxLength={300} value={profile.bio || ""} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} className="w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs resize-none focus:outline-none" placeholder="Who is this citizen?" /></div>
            </div>
            <div>
              <p className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase mb-2 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Privacy — you choose what the network sees</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PRIVACY_LABELS.map(([k, label, hint]) => (
                  <label key={k} data-testid={`privacy-${k}`} className="flex items-start gap-3 border border-white/10 px-3 py-2.5 cursor-pointer hover:border-white/25 transition-colors">
                    <input type="checkbox" className="mt-0.5" style={{ accentColor: acc }} checked={!!profile.privacy[k]} onChange={(e) => setProfile({ ...profile, privacy: { ...profile.privacy, [k]: e.target.checked } })} />
                    <span><span className="block font-mono text-[10px] text-zinc-200">{label}</span><span className="block font-mono text-[8px] text-zinc-500">{hint}</span></span>
                  </label>
                ))}
              </div>
            </div>
            <button data-testid="profile-save" disabled={busy} onClick={save} className="w-fit px-6 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-black disabled:opacity-40" style={{ backgroundColor: acc }}>
              {busy ? "…" : "Save profile"}
            </button>
            <p className="font-mono text-[8px] text-zinc-600">Avatar & banner uploads are AI-scanned before they go live — prohibited content is rejected and repeat rejections lead to a community ban.</p>
          </div>
        )}

        {/* public profile: actions */}
        {!mine && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="border border-white/10 bg-black/40 p-4 flex flex-col gap-2">
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5"><Mail className="w-3 h-3" /> Message</p>
              <textarea data-testid="msg-input" rows={2} maxLength={500} value={msg} onChange={(e) => setMsg(e.target.value)} className="w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs resize-none focus:outline-none" placeholder="Lands in their main-menu inbox…" />
              <button data-testid="msg-send" onClick={sendMessage} className="py-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black flex items-center justify-center gap-1.5" style={{ backgroundColor: acc }}><Send className="w-3 h-3" /> Send</button>
            </div>
            <div className="border border-white/10 bg-black/40 p-4 flex flex-col gap-2">
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5"><UsersIcon className="w-3 h-3" /> Invite to my server</p>
              {myServers.length === 0 ? <p className="font-mono text-[9px] text-zinc-600">You don't own a server — invites are an owner tool (5/day, anti-spam).</p> : (
                <>
                  <select data-testid="invite-server" value={inviteServer} onChange={(e) => setInviteServer(e.target.value)} className="bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs focus:outline-none">
                    <option value="">Pick a server…</option>
                    {myServers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <button data-testid="invite-send" onClick={sendInvite} className="py-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] border border-white/20 hover:border-white/50 transition-colors">Send invite</button>
                </>
              )}
            </div>
            <div className="border border-red-900/40 bg-red-950/10 p-4 flex flex-col gap-2">
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-red-400 flex items-center gap-1.5"><Flag className="w-3 h-3" /> Report</p>
              <input data-testid="report-input" maxLength={60} value={report} onChange={(e) => setReport(e.target.value)} className="w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs focus:outline-none" placeholder="Reason (e.g. NSFW avatar)" />
              <button data-testid="report-send" onClick={sendReport} className="py-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors">Report to studio staff</button>
              <p className="font-mono text-[8px] text-zinc-600">Community sanctions affect social features only — never their game access.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
